import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ActivityStatusEnum } from '../../../../gql/typings';

type PersonCustomFieldValue = {
  optionId?: number;
  newValue?: string;
};

type PersonCustomFieldRow = {
  id: string;
  customFieldCode?: string;
  values: PersonCustomFieldValue[];
  addNewOption: boolean;
  replace: boolean;
  maxValue: boolean;
};

export type ActivityImportMappingValue = {
  heading: string;
  typeCode: string;
  status: ActivityStatusEnum;
  startDate: string;
  endDate: string;
  responsibleUserIds: number[];
  productIds: number[];
  locationIds: number[];
  note?: string;
  customFields: Array<{
    customFieldCode: string;
    values: PersonCustomFieldValue[];
    addNewOption: boolean;
    replace: boolean;
    maxValue: boolean;
  }>;
};

type ActivityImportMappingProps = {
  projectId: number;
  fileName?: string;
  setValue: (value?: ActivityImportMappingValue) => void;
};

type DataQuery = {
  viewer?: {
    user: {
      id: number;
    };
  };
  importProject?: {
    id: number;
    files: {
      nodes: Array<{
        name?: string|null;
      }>;
    };
  }|null;
  activityTypes: {
    nodes: Array<{
      code: string;
      heading: string;
      standardTimeLengthMinutes?: number|null;
    }>;
  };
  products: {
    nodes: Array<{
      id: number;
      heading: string;
      code: string;
    }>;
  };
  sites: {
    nodes: Array<{
      id: number;
      name: string;
    }>;
  };
  users: {
    nodes: Array<{
      id: number;
      fullname: string;
    }>;
  };
  customFields: {
    nodes: Array<{
      code: string;
      heading: string;
      fieldTypeEnum: string;
      allowMultiple?: boolean|null;
      allowUserCreate?: boolean|null;
      isCheckableTag?: boolean|null;
      options?: {
        hash: string;
        nodes: Array<{
          id: number;
          value?: {
            id: number;
            value: string;
          }|null;
        }>;
      }|null;
    }>;
  };
};

const ActivityImportMapping: React.FC<ActivityImportMappingProps> = ({ projectId, fileName, setValue }) => {
  const { data, loading } = useQuery<DataQuery>(DATA_QUERY, {
    variables: { projectId },
  });
  const [form] = Form.useForm();
  const [customFields, setCustomFields] = useState<PersonCustomFieldRow[]>([]);

  const defaultHeading = useMemo(() => {
    const name = fileName || data?.importProject?.files.nodes[0]?.name || '';
    if (name.startsWith('upload-')) return 'Imported activity';
    return name.replace(/\.[^.]+$/, '') || 'Imported activity';
  }, [data, fileName]);

  const customFieldsByCode = useMemo(() => new Map(
    (data?.customFields.nodes ?? []).map(field => [field.code, field])
  ), [data]);
  const isSingleValue = (field?: DataQuery['customFields']['nodes']['0']) => field?.allowMultiple !== true;
  const isMultiValue = (field?: DataQuery['customFields']['nodes']['0']) => field?.allowMultiple === true;
  const isSingleValueNumber = (field?: DataQuery['customFields']['nodes']['0']) => (
    !!field && isSingleValue(field) && field.fieldTypeEnum === 'NUMBER'
  );
  const isSingleValueText = (field?: DataQuery['customFields']['nodes']['0']) => (
    !!field && isSingleValue(field) && field.fieldTypeEnum === 'TEXT'
  );
  const isOptionField = (field?: DataQuery['customFields']['nodes']['0']) => (
    !!field && ['CHECK', 'SELECT', 'TAG'].includes(field.fieldTypeEnum)
  );
  const canAddNewOption = (field?: DataQuery['customFields']['nodes']['0']) => (
    isOptionField(field) && field?.allowUserCreate === true
  );

  useEffect(() => {
    if (!data) return;
    const start = dayjs().second(0);
    const type = data.activityTypes.nodes[0];
    form.setFieldsValue({
      heading: defaultHeading,
      typeCode: type?.code,
      status: ActivityStatusEnum.DONE,
      dateRange: [
        start,
        start.add(type?.standardTimeLengthMinutes ?? 0, 'minute'),
      ],
      responsibleUserIds: data.viewer?.user.id ? [data.viewer.user.id] : [],
      productIds: [],
      locationIds: [],
      note: undefined,
    });
  }, [data, defaultHeading, form]);

  const emitValue = async () => {
    try {
      const values = await form.validateFields();
      const dateRange = values.dateRange as [Dayjs, Dayjs];
      const invalidCustomFields = customFields.filter(row => row.customFieldCode && row.values.length === 0);
      if (invalidCustomFields.length > 0) {
        setValue(undefined);
        return;
      }

      setValue({
        heading: values.heading,
        typeCode: values.typeCode,
        status: values.status,
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
        responsibleUserIds: values.responsibleUserIds ?? [],
        productIds: values.productIds ?? [],
        locationIds: values.locationIds ?? [],
        note: values.note,
        customFields: customFields
          .filter(row => row.customFieldCode && row.values.length > 0)
          .map(row => ({
            customFieldCode: row.customFieldCode!,
            values: row.values,
            addNewOption: row.addNewOption,
            replace: row.replace,
            maxValue: row.maxValue,
          })),
      });
    } catch {
      setValue(undefined);
    }
  };

  const updateCustomField = (id: string, patch: Partial<PersonCustomFieldRow>) => {
    setCustomFields(prev => prev.map(row => {
      if (row.id !== id) return row;
      const next = { ...row, ...patch };
      const selectedField = next.customFieldCode ? customFieldsByCode.get(next.customFieldCode) : undefined;
      if (patch.customFieldCode !== undefined) {
        next.values = selectedField?.fieldTypeEnum === 'SWITCH' ? [{ newValue: 'false' }] : [];
        next.replace = isSingleValue(selectedField) && !isSingleValueText(selectedField);
        next.maxValue = false;
      }
      if (!canAddNewOption(selectedField)) next.addNewOption = false;
      if (patch.addNewOption === false && isOptionField(selectedField)) {
        next.values = next.values.filter(value => value.optionId !== undefined);
      }
      if (!isSingleValueNumber(selectedField)) next.maxValue = false;
      if (isSingleValue(selectedField) && !isSingleValueText(selectedField)) next.replace = true;
      return next;
    }));
  };

  const removeCustomField = (id: string) => {
    setCustomFields(prev => prev.filter(row => row.id !== id));
  };

  useEffect(() => {
    if (!data) return;
    // setFieldsValue does not trigger onValuesChange; validate the loaded defaults too.
    emitValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, data, defaultHeading]);

  return (
    <div>
      <h2>Activity details</h2>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={emitValue}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="heading" label="Heading" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="typeCode" label="Activity type" rules={[{ required: true }]}>
              <Select
                loading={loading}
                options={(data?.activityTypes.nodes ?? []).map(type => ({
                  value: type.code,
                  label: type.heading,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="status" label="Activity status" rules={[{ required: true }]}>
              <Select
                options={Object.values(ActivityStatusEnum).map(status => ({
                  value: status,
                  label: status,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="dateRange" label="Date / start / end" rules={[{ required: true }]}>
              <DatePicker.RangePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="responsibleUserIds" label="Responsible user" rules={[{ required: true }]}>
              <Select
                mode="multiple"
                loading={loading}
                options={(data?.users.nodes ?? []).map(user => ({
                  value: user.id,
                  label: user.fullname,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="productIds" label="Product">
              <Select
                mode="multiple"
                loading={loading}
                options={(data?.products.nodes ?? []).map(product => ({
                  value: product.id,
                  label: product.heading,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="locationIds" label="Location">
              <Select
                mode="multiple"
                loading={loading}
                showSearch
                optionFilterProp="label"
                options={(data?.sites.nodes ?? []).map(site => ({
                  value: site.id,
                  label: site.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="note" label="Note">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <h2>Person custom fields</h2>
      <Space direction="vertical" size={0} style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">
          <b>New option:</b>
          {' '}
          checked creates new options; unchecked uses existing options only.
        </Typography.Text>
        <Typography.Text type="secondary">
          <b>Replace:</b>
          {' '}
          checked replaces existing person values; unchecked adds to them. Single-value Text fields can append to existing text.
        </Typography.Text>
        <Typography.Text type="secondary">
          <b>Max value:</b>
          {' '}
          for single-value Number fields, saves only higher values.
        </Typography.Text>
      </Space>
      <Table<PersonCustomFieldRow>
        dataSource={customFields}
        pagination={false}
        rowKey="id"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No data"
              style={{ margin: '10px 0' }}
            />
          ),
        }}
        columns={[
          {
            title: 'Custom field',
            render: (_, row) => (
              <Select
                value={row.customFieldCode}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                options={(data?.customFields.nodes ?? []).map(field => ({
                  value: field.code,
                  label: field.heading,
                }))}
                onChange={customFieldCode => updateCustomField(row.id, { customFieldCode })}
              />
            ),
          },
          {
            title: 'Value',
            render: (_, row) => {
              const selectedField = row.customFieldCode ? customFieldsByCode.get(row.customFieldCode) : undefined;
              if (!selectedField) return null;

              const firstNewValue = row.values[0]?.newValue;
              if (selectedField.fieldTypeEnum === 'TEXT') {
                return (
                  <Input
                    value={firstNewValue}
                    onChange={event => updateCustomField(row.id, {
                      values: event.target.value ? [{ newValue: event.target.value }] : [],
                    })}
                  />
                );
              }

              if (selectedField.fieldTypeEnum === 'NUMBER') {
                return (
                  <InputNumber
                    min={0}
                    value={firstNewValue ? Number(firstNewValue) : null}
                    onChange={value => updateCustomField(row.id, {
                      values: value === null ? [] : [{ newValue: value.toString() }],
                    })}
                    style={{ width: '100%' }}
                  />
                );
              }

              if (selectedField.fieldTypeEnum === 'SWITCH') {
                return (
                  <Switch
                    checked={firstNewValue === 'true'}
                    onChange={checked => updateCustomField(row.id, {
                      values: [{ newValue: checked ? 'true' : 'false' }],
                    })}
                  />
                );
              }

              if (!isOptionField(selectedField)) return null;

              const isMultiple = isMultiValue(selectedField);
              const allowCustomValue = canAddNewOption(selectedField) && row.addNewOption;
              const optionValues = row.values
                .map(value => value.optionId ?? value.newValue)
                .filter((value): value is number|string => value !== undefined);
              const options = (selectedField.options?.nodes ?? []).flatMap(option => (
                option.value ? [{ value: option.id, label: option.value.value }] : []
              ));
              const updateOptionValues = (values: Array<number|string>) => {
                const nextValues = isMultiple ? values : values.slice(-1);
                updateCustomField(row.id, {
                  values: nextValues.map(value => (
                    typeof value === 'number' ? { optionId: value } : { newValue: value }
                  )),
                });
              };

              if (selectedField.fieldTypeEnum === 'CHECK' && !allowCustomValue) {
                if (selectedField.isCheckableTag) {
                  return (
                    <Space size={[0, 4]} wrap>
                      {options.map(option => (
                        <Tag.CheckableTag
                          key={option.value}
                          checked={optionValues.includes(option.value)}
                          onChange={checked => updateOptionValues(checked
                            ? [...optionValues, option.value]
                            : optionValues.filter(value => value !== option.value))}
                        >
                          {option.label}
                        </Tag.CheckableTag>
                      ))}
                    </Space>
                  );
                }

                if (isMultiple) {
                  return (
                    <Checkbox.Group
                      value={optionValues as number[]}
                      onChange={values => updateOptionValues(values as number[])}
                    >
                      <Space direction="vertical">
                        {options.map(option => (
                          <Checkbox key={option.value} value={option.value}>
                            {option.label}
                          </Checkbox>
                        ))}
                      </Space>
                    </Checkbox.Group>
                  );
                }

                return (
                  <Radio.Group
                    value={optionValues[0]}
                    onChange={event => updateOptionValues([event.target.value])}
                  >
                    <Space direction="vertical">
                      {options.map(option => (
                        <Radio key={option.value} value={option.value}>
                          {option.label}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                );
              }

              if (selectedField.fieldTypeEnum === 'TAG' && !allowCustomValue) {
                return (
                  <Space size={[0, 4]} wrap>
                    {options.map(option => (
                      <Tag.CheckableTag
                        key={option.value}
                        checked={optionValues.includes(option.value)}
                        onChange={checked => updateOptionValues(checked
                          ? [...optionValues, option.value]
                          : optionValues.filter(value => value !== option.value))}
                      >
                        {option.label}
                      </Tag.CheckableTag>
                    ))}
                  </Space>
                );
              }

              const useArrayValue = isMultiple || allowCustomValue;
              return (
                <Select
                  mode={useArrayValue ? (allowCustomValue ? 'tags' : 'multiple') : undefined}
                  value={useArrayValue ? optionValues : optionValues[0]}
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={options}
                  onChange={value => {
                    const values = Array.isArray(value) ? value : [value];
                    updateOptionValues(values.filter((item): item is number|string => item !== undefined));
                  }}
                  style={{ width: '100%' }}
                />
              );
            },
          },
          {
            title: 'New option',
            width: 120,
            render: (_, row) => {
              const selectedField = row.customFieldCode ? customFieldsByCode.get(row.customFieldCode) : undefined;
              const disabled = !canAddNewOption(selectedField);

              return (
                <Checkbox
                  checked={row.addNewOption && !disabled}
                  disabled={disabled}
                  onChange={event => updateCustomField(row.id, { addNewOption: event.target.checked })}
                />
              );
            },
          },
          {
            title: 'Replace',
            width: 100,
            render: (_, row) => {
              const selectedField = row.customFieldCode ? customFieldsByCode.get(row.customFieldCode) : undefined;
              const isForcedReplace = isSingleValue(selectedField) && !isSingleValueText(selectedField);
              const disabled = !selectedField || isForcedReplace;

              return (
                <Checkbox
                  checked={selectedField ? isForcedReplace || row.replace : false}
                  disabled={disabled}
                  onChange={event => {
                    if (isMultiValue(selectedField) || isSingleValueText(selectedField)) {
                      updateCustomField(row.id, { replace: event.target.checked });
                    }
                  }}
                />
              );
            },
          },
          {
            title: 'Max value',
            width: 110,
            render: (_, row) => {
              const selectedField = row.customFieldCode ? customFieldsByCode.get(row.customFieldCode) : undefined;
              const disabled = !isSingleValueNumber(selectedField);

              return (
                <Checkbox
                  checked={row.maxValue && !disabled}
                  disabled={disabled}
                  onChange={event => updateCustomField(row.id, { maxValue: event.target.checked })}
                />
              );
            },
          },
          {
            title: '',
            width: 100,
            render: (_, row) => (
              <Button danger onClick={() => removeCustomField(row.id)}>
                Remove
              </Button>
            ),
          },
        ]}
      />
      <Button
        style={{ marginTop: 12 }}
        onClick={() => setCustomFields(prev => [
          ...prev,
          {
            id: `${Date.now()}-${prev.length}`,
            values: [],
            addNewOption: false,
            replace: false,
            maxValue: false,
          },
        ])}
      >
        Add person custom field
      </Button>
    </div>
  );
};

const DATA_QUERY = gql`
  query ActivityImportMappingData($projectId: Int!) {
    viewer {
      user {
        id
      }
    }
    importProject(id: $projectId) {
      id
      files {
        nodes {
          name
        }
      }
    }
    activityTypes {
      nodes {
        code
        heading
        standardTimeLengthMinutes
      }
    }
    products(criteria: {}) {
      nodes {
        id
        code
        heading
      }
    }
    sites(criteria: { fetchSize: { limit: 10000 } }) {
      nodes {
        id
        name
      }
    }
    users(criteria: { fetchSize: { limit: 10000 } }) {
      nodes {
        id
        fullname
      }
    }
    customFields(criteria: { entityType: PERSON, isActive: true }) {
      nodes {
        code
        heading
        fieldTypeEnum
        allowMultiple
        allowUserCreate
        isCheckableTag
        options(criteria: { fetchSize: { limit: null } }) {
          hash
          nodes {
            id
            value {
              id
              value
            }
          }
        }
      }
    }
  }
`;

export default ActivityImportMapping;
